import { useEffect, useState, useCallback, useRef } from "react";
import { makeRequest } from "../makeRequest";

const UNAUTHORIZED_CODES = [400, 401, 403, 404];
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const useFetchPost = (url, postData, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const activeRequestRef = useRef(null);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  }, []);

  const postWithRetry = useCallback(async (retryCount = 0) => {
    // Cancel previous request if exists
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
    }

    // Create new abort controller
    const abortController = new AbortController();
    activeRequestRef.current = abortController;

    try {
      const response = await makeRequest.post(url, postData, {
        signal: abortController.signal,
        ...options
      });
      
      // Reset active request ref after successful completion
      activeRequestRef.current = null;

      // Handle 400 status separately from unauthorized codes
      if (response.status === 400) {
        setError({
          message: response.data?.message || "Bad Request",
          code: response.status
        });
        return null;
      }
      
      if (UNAUTHORIZED_CODES.includes(response.data?.code)) {
        handleUnauthorized();
        return;
      }

      setData(response.data);
      return response.data;

    } catch (err) {
      // Ignore aborted requests
      if (err.name === 'AbortError') {
        return;
      }

      // Reset active request ref on error
      activeRequestRef.current = null;

      // Enhanced error handling
      const errorResponse = err.response?.data || {
        message: "Network Error",
        code: "NETWORK_ERROR",
        status_code: 0,
        success: false
      };

      if (err.response?.status === 400) {
        setError(errorResponse);
        return null;
      }

      if (err.response && UNAUTHORIZED_CODES.includes(err.code)) {
        handleUnauthorized();
        return;
      }

      if (retryCount < MAX_RETRIES && options.retry !== false) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return postWithRetry(retryCount + 1);
      }

      setError(err.response?.data || {
        message: "Network Error",
        code: "NETWORK_ERROR",
        status_code: 0,
        success: false
      });
      throw err;
    }
  }, [url, postData, options.retry, handleUnauthorized]);

  useEffect(() => {
    let isSubscribed = true;

    const fetchData = async () => {
      if (!url || !postData) return;

      setLoading(true);
      setError(null);

      try {
        await postWithRetry();
      } catch (err) {
        if (isSubscribed) {
          console.error('Request failed:', err);
        }
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isSubscribed = false;
      if (activeRequestRef.current) {
        activeRequestRef.current.abort();
      }
    };
  }, [url, postData, postWithRetry]);

  return { data, loading, error, refetch: () => postWithRetry() };
};

export default useFetchPost;
